import React, { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import TrackCard from "../components/TrackCard";
import { Music, Download, Crown, Plus, Trash2 } from "lucide-react";
import { usePlayer } from "../context/PlayerContext";

export default function Profile() {
  const { user } = useAuth();
  const { playTrack } = usePlayer();
  const [library, setLibrary] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [tracksMap, setTracksMap] = useState({});
  const [newPlaylist, setNewPlaylist] = useState("");

  const load = async () => {
    try {
      const [lib, pls, tracks] = await Promise.all([
        api.get("/me/library"),
        api.get("/playlists"),
        api.get("/tracks"),
      ]);
      setLibrary(lib.data);
      setPlaylists(pls.data);
      const map = {}; tracks.data.forEach((t) => (map[t.id] = t));
      setTracksMap(map);
    } catch (err) {
      console.error("Profile load failed:", err);
    }
  };

  useEffect(() => { load(); }, []);

  const createPlaylist = async () => {
    if (!newPlaylist.trim()) return;
    try {
      const { data } = await api.post("/playlists", { name: newPlaylist });
      setPlaylists([...playlists, data]);
      setNewPlaylist("");
      toast.success("Playlist created");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removePlaylist = async (id) => {
    await api.delete(`/playlists/${id}`);
    setPlaylists(playlists.filter((p) => p.id !== id));
  };

  if (!user) return null;
  const subExpired = user.subscription_expires && new Date(user.subscription_expires) < new Date();

  return (
    <div className="container-x py-16">
      <div className="grid md:grid-cols-3 gap-8 mb-12">
        <div className="md:col-span-2">
          <div className="eyebrow mb-4">// Profile</div>
          <h1 className="section-heading mb-2">Hi, {user.name}</h1>
          <p className="text-white/50 font-mono-plex text-sm">{user.email}</p>
        </div>
        <div className="border border-white/10 bg-[#121217] p-6">
          <div className="flex items-center gap-2 mb-4">
            <Crown className="w-4 h-4 text-[#FF5E00]" />
            <span className="eyebrow">Your plan</span>
          </div>
          <div className="font-display font-black text-2xl uppercase" data-testid="profile-plan">
            {user.subscription_plan && !subExpired ? user.subscription_plan : "Free"}
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className="text-4xl font-display font-black text-[#B3FF00]" data-testid="profile-credits">{user.credits ?? 0}</span>
            <span className="text-xs font-mono-plex uppercase tracking-widest text-white/50">credits</span>
          </div>
        </div>
      </div>

      {/* Library */}
      <section className="mb-16">
        <div className="flex items-center gap-3 mb-6">
          <Download className="w-5 h-5 text-[#FF5E00]" />
          <h2 className="font-display font-bold text-2xl uppercase">Your Library</h2>
        </div>
        {library.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center text-white/40 font-mono-plex text-sm">
            No tracks purchased yet. Browse the catalog and unlock your first track!
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="profile-library">
            {library.map((t) => <TrackCard key={t.id} track={t} />)}
          </div>
        )}
      </section>

      {/* Playlists */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <Music className="w-5 h-5 text-[#B3FF00]" />
          <h2 className="font-display font-bold text-2xl uppercase">Your Playlists</h2>
        </div>
        <div className="flex gap-2 mb-6">
          <input value={newPlaylist} onChange={(e) => setNewPlaylist(e.target.value)}
            placeholder="New playlist name..."
            className="flex-1 bg-[#121217] border border-white/10 px-4 py-3 focus:outline-none focus:border-[#FF5E00]"
            data-testid="new-playlist-input" />
          <button onClick={createPlaylist} className="btn-primary flex items-center gap-2" data-testid="new-playlist-btn">
            <Plus className="w-4 h-4" /> Create
          </button>
        </div>
        <div className="space-y-4">
          {playlists.map((pl) => (
            <div key={pl.id} className="border border-white/10 bg-[#121217] p-6" data-testid={`playlist-${pl.id}`}>
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-display font-bold text-xl">{pl.name}</h3>
                  <span className="text-xs font-mono-plex text-white/40">{pl.track_ids.length} tracks</span>
                </div>
                <button onClick={() => removePlaylist(pl.id)} className="text-white/30 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {pl.track_ids.map((tid) => {
                  const t = tracksMap[tid]; if (!t) return null;
                  return (
                    <button key={tid} onClick={() => playTrack(t, { preview: true })}
                      className="flex items-center gap-2 px-3 py-2 bg-black/40 border border-white/5 hover:border-[#B3FF00]/40 text-sm">
                      <img src={t.cover_url} alt="" className="w-8 h-8 object-cover" />
                      <span className="truncate max-w-[160px]">{t.title}</span>
                    </button>
                  );
                })}
                {pl.track_ids.length === 0 && (
                  <span className="text-xs text-white/30 font-mono-plex">Empty — add tracks from the catalog</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>
      <div className="h-32" />
    </div>
  );
}
